import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { VerisureQrCode } from './VerisureQrCode';
import { toast } from 'react-hot-toast';
import { EquipmentSize } from '../../types';
import './VerisureQrCard.css';

export interface VerisureQrCardProps {
  /** The value/URL encoded into the QR code */
  value: string;
  /** The Asset ID display (e.g. VRS-001, ITM-1029, CAB-101) */
  assetId?: string;
  /** Optional title or item name (e.g. "Fujikura 90S+ Splicing Machine") */
  title?: string;
  /** Optional subtitle or category (e.g. "Consumer Electronics") */
  subtitle?: string;
  /** Optional custom logo image URL, defaults to /official.png */
  logoUrl?: string;
  /** Equipment determined card size ('compact' | 'standard' | 'large' | 'industrial') */
  size?: EquipmentSize;
  /** Whether to show the equipment size switch tabs */
  showSizeSelector?: boolean;
  /** Whether to show print/download action buttons below the card */
  showActions?: boolean;
  /** Custom class for wrapping container */
  className?: string;
  /** Card ID for custom print targets */
  cardId?: string;
}

export const VerisureQrCard: React.FC<VerisureQrCardProps> = ({
  value,
  assetId,
  title,
  subtitle,
  logoUrl = '/official.png',
  size = 'standard',
  showSizeSelector = true,
  showActions = true,
  className = '',
  cardId = 'verisure-qr-card',
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [activeSize, setActiveSize] = useState<EquipmentSize>(size);

  useEffect(() => {
    setActiveSize(size);
  }, [size]);

  // Extract fallback asset ID from value if not explicitly given
  const derivedAssetId =
    assetId ||
    (value ? value.split('/').filter(Boolean).pop() || 'VRS-001' : 'VRS-001');

  const handlePrint = () => {
    document.body.classList.add('printing-verisure-card');
    const cleanup = () => {
      document.body.classList.remove('printing-verisure-card');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 2000);
  };

  const handleSavePdf = () => {
    const originalTitle = document.title;
    document.title = `VERISURE-Identity-Card-${derivedAssetId}-${activeSize.toUpperCase()}`;
    toast.success('In the print dialog, select "Save as PDF" to download your card PDF.', { duration: 4500 });
    document.body.classList.add('printing-verisure-card');
    const cleanup = () => {
      document.title = originalTitle;
      document.body.classList.remove('printing-verisure-card');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 2500);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Verification URL copied to clipboard!');
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  // Dimensions configuration by equipment size
  const sizeConfig = {
    compact: {
      width: 290,
      height: 420,
      qrBoxSize: 210,
      qrSize: 180,
      logoHeight: 48,
      fontSizeTitle: 22,
      fontInfoSize: 9,
      cornerSwoosh: 90,
    },
    standard: {
      width: 430,
      height: 620,
      qrBoxSize: 320,
      qrSize: 290,
      logoHeight: 70,
      fontSizeTitle: 30,
      fontInfoSize: 11,
      cornerSwoosh: 140,
    },
    large: {
      width: 540,
      height: 750,
      qrBoxSize: 410,
      qrSize: 360,
      logoHeight: 88,
      fontSizeTitle: 38,
      fontInfoSize: 13,
      cornerSwoosh: 170,
    },
    industrial: {
      width: 650,
      height: 890,
      qrBoxSize: 500,
      qrSize: 450,
      logoHeight: 105,
      fontSizeTitle: 46,
      fontInfoSize: 15,
      cornerSwoosh: 210,
    },
  }[activeSize];

  const handleDownloadPng = () => {
    try {
      const svgElement = cardRef.current?.querySelector('svg.verisure-qr-svg') as SVGElement | null;
      if (!svgElement) {
        toast.error('Could not render QR image for download');
        return;
      }

      const canvas = document.createElement('canvas');
      const scale = 2; // 2x ultra high-res
      const width = sizeConfig.width * scale;
      const height = sizeConfig.height * scale;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clean White Card Background
      ctx.fillStyle = '#ffffff';
      const radius = (activeSize === 'compact' ? 20 : activeSize === 'industrial' ? 38 : 28) * scale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(0, 0, width, height, radius);
        ctx.fill();
      } else {
        ctx.fillRect(0, 0, width, height);
      }

      // Outer Orange Border
      ctx.lineWidth = (activeSize === 'industrial' ? 5 : 4) * scale;
      ctx.strokeStyle = '#ff5a00';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(2 * scale, 2 * scale, width - 4 * scale, height - 4 * scale, radius);
        ctx.stroke();
      }

      // Subtle Inset Border
      ctx.lineWidth = 1 * scale;
      ctx.strokeStyle = 'rgba(255, 90, 0, 0.2)';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(10 * scale, 10 * scale, width - 20 * scale, height - 20 * scale, (radius - 8 * scale));
        ctx.stroke();
      }

      // Corner Fire Swooshes on Bottom Left and Bottom Right
      const swooshRadius = sizeConfig.cornerSwoosh * scale;
      const leftGrad = ctx.createRadialGradient(0, height, 0, 0, height, swooshRadius);
      leftGrad.addColorStop(0, '#e51b12');
      leftGrad.addColorStop(0.5, '#ff5a00');
      leftGrad.addColorStop(0.8, '#ffb800');
      leftGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = leftGrad;
      ctx.beginPath();
      ctx.arc(0, height, swooshRadius, Math.PI * 1.5, Math.PI * 2);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.fill();

      const rightGrad = ctx.createRadialGradient(width, height, 0, width, height, swooshRadius);
      rightGrad.addColorStop(0, '#e51b12');
      rightGrad.addColorStop(0.5, '#ff5a00');
      rightGrad.addColorStop(0.8, '#ffb800');
      rightGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = rightGrad;
      ctx.beginPath();
      ctx.arc(width, height, swooshRadius, Math.PI, Math.PI * 1.5);
      ctx.lineTo(width, height);
      ctx.closePath();
      ctx.fill();

      // Load Official Logo and Draw
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        const logoHeight = sizeConfig.logoHeight * scale;
        const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
        const logoX = (width - logoWidth) / 2;
        const logoY = (activeSize === 'compact' ? 18 : 28) * scale;
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

        // Identity Text "I am Verisure"
        const identityY = logoY + logoHeight + (activeSize === 'compact' ? 24 : 36) * scale;
        ctx.font = `800 ${sizeConfig.fontSizeTitle * scale}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        
        const offset = activeSize === 'compact' ? 38 * scale : activeSize === 'industrial' ? 80 * scale : 58 * scale;
        ctx.fillStyle = '#17191d';
        ctx.fillText('I am ', width / 2 - offset, identityY);

        ctx.fillStyle = '#ff5a00';
        ctx.fillText('Verisure', width / 2 + (offset * 0.55), identityY);

        // Orange underline under Verisure
        ctx.fillStyle = '#ff5a00';
        ctx.fillRect(width / 2 - (offset * 0.4), identityY + 6 * scale, offset * 1.9, 3 * scale);

        // QR Frame Box
        const qrBoxSize = sizeConfig.qrBoxSize * scale;
        const qrBoxX = (width - qrBoxSize) / 2;
        const qrBoxY = identityY + (activeSize === 'compact' ? 16 : 24) * scale;
        ctx.fillStyle = '#ffffff';
        ctx.lineWidth = (activeSize === 'industrial' ? 4 : 3) * scale;
        ctx.strokeStyle = '#ff5a00';
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 20 * scale);
          ctx.fill();
          ctx.stroke();
        } else {
          ctx.fillRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);
          ctx.strokeRect(qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);
        }

        // Draw QR Code
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const DOMURL = window.URL || window.webkitURL || window;
        const qrUrl = DOMURL.createObjectURL(svgBlob);
        const qrImg = new Image();

        qrImg.onload = () => {
          const qrSize = sizeConfig.qrSize * scale;
          const qrX = (width - qrSize) / 2;
          const qrY = qrBoxY + (qrBoxSize - qrSize) / 2;
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
          DOMURL.revokeObjectURL(qrUrl);

          // Asset info underneath QR
          const infoY = qrBoxY + qrBoxSize + (activeSize === 'compact' ? 16 : 24) * scale;
          ctx.fillStyle = '#64748b';
          ctx.font = `bold ${sizeConfig.fontInfoSize * scale}px Arial, sans-serif`;
          ctx.fillText(`VERISURE ASSET ID: ${derivedAssetId} • [${activeSize.toUpperCase()}]`, width / 2, infoY);

          // Download canvas as PNG
          const pngUrl = canvas.toDataURL('image/png');
          const downloadLink = document.createElement('a');
          downloadLink.download = `VERISURE-${activeSize.toUpperCase()}-${derivedAssetId}.png`;
          downloadLink.href = pngUrl;
          downloadLink.click();
          toast.success(`Identity Badge [${activeSize.toUpperCase()}] for ${derivedAssetId} downloaded!`);
        };

        qrImg.src = qrUrl;
      };

      logoImg.src = logoUrl || '/official.png';
    } catch (err: any) {
      console.error('Download error', err);
      toast.error('Could not download image: ' + err.message);
    }
  };

  return (
    <div className={`verisure-card-container ${className}`}>
      {/* Optional Equipment Size Selector */}
      {showSizeSelector && (
        <div className="verisure-size-selector no-print">
          <button
            type="button"
            onClick={() => setActiveSize('compact')}
            className={`verisure-size-btn ${activeSize === 'compact' ? 'active' : ''}`}
            title="Compact footprint (Handheld devices, sensors, tools ~ 35-45mm)"
          >
            Compact
          </button>
          <button
            type="button"
            onClick={() => setActiveSize('standard')}
            className={`verisure-size-btn ${activeSize === 'standard' ? 'active' : ''}`}
            title="Standard Equipment Badge (Laptops, household appliances, mini-gensets ~ 85mm)"
          >
            Standard
          </button>
          <button
            type="button"
            onClick={() => setActiveSize('large')}
            className={`verisure-size-btn ${activeSize === 'large' ? 'active' : ''}`}
            title="Large Placard (Server cabinets, commercial ACs, vehicles ~ 120-140mm)"
          >
            Large
          </button>
          <button
            type="button"
            onClick={() => setActiveSize('industrial')}
            className={`verisure-size-btn ${activeSize === 'industrial' ? 'active' : ''}`}
            title="Industrial Plant Plate (Turbines, telecom towers, heavy generators ~ 180-220mm)"
          >
            Industrial
          </button>
        </div>
      )}

      {/* Main Card matching reference template with size variant class */}
      <div
        className={`verisure-card size-${activeSize} verisure-card-printable`}
        id={cardId}
        ref={cardRef}
      >
        {/* Top Official Logo */}
        <div className="logo-area">
          <img
            src={logoUrl || '/official.png'}
            className="logo"
            alt="Verisure - Secure. Assured. Always."
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/official.png';
            }}
          />
        </div>

        {/* Identity Text */}
        <div className="identity">
          I am <span className="brand">Verisure</span>
        </div>

        {/* Optional Title / Subtitle */}
        {(title || subtitle) && (
          <div className="identity-meta">
            {title && <span>{title}</span>}
            {title && subtitle && <span> • </span>}
            {subtitle && <span className="text-slate-500">{subtitle}</span>}
          </div>
        )}

        {/* QR Frame */}
        <div className="qr-frame">
          <div className="qr-inner" id="qrcode">
            <VerisureQrCode
              className="verisure-qr-svg"
              value={value || 'https://verisure.online'}
              size={sizeConfig.qrSize}
              level="H"
              includeMargin={false}
              showCenterBadge={true}
              badgeTheme="light"
            />
          </div>
        </div>

        {/* Asset Information */}
        <div className="asset-info">
          VERISURE ASSET ID: <strong id="assetId">{derivedAssetId}</strong>
        </div>

        <div className="asset-url">{value}</div>

        {/* Corner Fire Swooshes */}
        <div className="fire-corner-left"></div>
        <div className="fire-corner-right"></div>
        <div className="fire-base"></div>
      </div>

      {/* Action Buttons Bar */}
      {showActions && (
        <div className="verisure-actions-bar no-print">
          <button
            type="button"
            onClick={handlePrint}
            className="verisure-action-btn primary"
            title={`Print ${activeSize.toUpperCase()} Badge`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print Card ({activeSize.toUpperCase()})
          </button>

          <button
            type="button"
            onClick={handleSavePdf}
            className="verisure-action-btn secondary"
            title="Save high-resolution PDF for printing or archiving"
          >
            <svg
              className="w-4 h-4 text-orange-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            Save as PDF
          </button>

          <button
            type="button"
            onClick={handleDownloadPng}
            className="verisure-action-btn secondary"
            title="Download high-resolution PNG for Sticker/Laminating"
          >
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download PNG
          </button>

          <button
            type="button"
            onClick={handleCopyLink}
            className="verisure-action-btn secondary"
            title="Copy Verification URL"
          >
            <svg
              className="w-4 h-4 text-orange-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copy Link
          </button>
        </div>
      )}
    </div>
  );
};


